import { Button, CodeField, ErrorBand, Field, RecoveryCodes, Turnstile, TwoFactorSetupForm } from '@bak/admin-ui'
import { LOGIN_CAPTCHA_ACTION } from '@bak/shared-utils'
import type { SessionUser, TwoFactorStep } from '@bak/types'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import { z } from 'zod'

import { api, ApiError, errorMessage } from '../../api/client.js'
import { authConfigQuery, meQuery } from '../../api/queries.js'
import { schemas } from '../../api/resources.js'
import { LanguageSwitch } from '../../i18n/LanguageSwitch.js'
import { useLang, useT } from '../../i18n/index.js'
import { twoFactorError, twoFactorLabels } from './two-factor.js'

export function LoginPage() {
  const t = useT()
  const { lang } = useLang()
  const search = useSearch({ from: '/login' })
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  // Two-factor (#227): after a correct password, the code (or set-up, when it is enforced). No session until then.
  const [step, setStep] = useState<TwoFactorStep | null>(null)
  // Turnstile (#228): the site key comes from the API (per site); without one (local dev) there is no widget.
  const captcha = useQuery(authConfigQuery)
  const siteKey = captcha.data?.siteKey ?? null
  const [token, setToken] = useState('')
  const [captchaRound, setCaptchaRound] = useState(0)
  const waitingForCaptcha = captcha.isPending || (siteKey !== null && !token)

  const signedIn = async (user: SessionUser) => {
    qc.setQueryData(meQuery.queryKey, { user })
    // Only same-app relative redirects.
    const to = search.redirect && z.string().startsWith('/').safeParse(search.redirect).success && !search.redirect.startsWith('//') ? search.redirect : '/'
    await navigate({ to })
  }

  const startOver = () => {
    setStep(null)
    setPassword('')
    setError(null)
    setCaptchaRound((n) => n + 1)
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setPending(true)
    setError(null)
    try {
      const result = await api('/auth/login', schemas.login, { method: 'POST', body: { email, password, ...(token ? { turnstileToken: token } : {}) } })
      if ('twoFactor' in result) setStep(result.twoFactor)
      else await signedIn(result.user)
    } catch (err) {
      setError(errorMessage(err))
      // A token works once: get a fresh one for the next try.
      setCaptchaRound((n) => n + 1)
    } finally {
      setPending(false)
    }
  }

  let card: ReactNode
  if (step === 'code') card = <CodeStep onSignedIn={signedIn} onStartOver={startOver} />
  else if (step === 'setup') card = <SetupStep onSignedIn={signedIn} onStartOver={startOver} />
  else
    card = (
      <form onSubmit={(e) => void submit(e)} className="mv-panel flex flex-col gap-4 !p-6">
        <div>
          <h1 className="font-[family-name:var(--mv-font-display)] text-xl font-bold text-[var(--mv-fg-strong)]">{t('auth.login.title')}</h1>
          <p className="mt-1 text-sm text-[var(--fg-muted)]">{t(captcha.data?.central ? 'auth.login.subtitleCentral' : 'auth.login.subtitle')}</p>
        </div>
        {error && <ErrorBand>{error}</ErrorBand>}
        <Field label={t('auth.login.email')} type="email" value={email} onChange={setEmail} isRequired />
        <Field label={t('auth.login.password')} type="password" value={password} onChange={setPassword} isRequired />
        {siteKey && (
          <div>
            <Turnstile siteKey={siteKey} action={LOGIN_CAPTCHA_ACTION} language={lang} onToken={setToken} resetKey={captchaRound} />
            {!token && <p className="mt-1 text-xs text-[var(--fg-muted)]">{t('auth.login.captchaHint')}</p>}
          </div>
        )}
        {captcha.isError && <ErrorBand>{errorMessage(captcha.error)}</ErrorBand>}
        <Button type="submit" isDisabled={pending || waitingForCaptcha} className="w-full">
          {pending ? t('auth.login.submitting') : t('auth.login.submit')}
        </Button>
      </form>
    )

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--mv-bg)] px-4 py-8">
      <LanguageSwitch className="absolute top-4 right-4" />
      <div className={step === 'setup' ? 'w-full max-w-md' : 'w-full max-w-sm'}>
        <div className="mb-6 flex items-center gap-3">
          <img src="/brand/logo-mark.svg" alt="" className="h-10 w-10" />
          <div>
            <p className="font-[family-name:var(--mv-font-display)] text-lg font-bold text-[var(--mv-fg-strong)]">{t('layout.brandName')}</p>
            <p className="text-xs text-[var(--fg-muted)]">{t('layout.brandSub')}</p>
          </div>
        </div>
        {card}
      </div>
    </div>
  )
}

function StartOver({ onPress }: { onPress: () => void }) {
  const t = useT()
  return (
    <button type="button" onClick={onPress} className="self-center text-sm font-medium text-[var(--mv-blue)] hover:underline">
      {t('auth.2fa.startOver')}
    </button>
  )
}

/** The code from the app, or a recovery code. A wrong one counts towards the lockout, like a wrong password. */
function CodeStep({ onSignedIn, onStartOver }: { onSignedIn: (user: SessionUser) => Promise<void>; onStartOver: () => void }) {
  const t = useT()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [pending, setPending] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setPending(true)
    setError(null)
    try {
      const { user } = await api('/auth/2fa/verify', schemas.me, { method: 'POST', body: { code } })
      await onSignedIn(user)
    } catch (err) {
      setError(twoFactorError(err, t))
      setExpired(!(err instanceof ApiError && err.status === 422))
      setCode('')
    } finally {
      setPending(false)
    }
  }

  return (
    <form onSubmit={(e) => void submit(e)} className="mv-panel flex flex-col gap-4 !p-6">
      <div>
        <h1 className="font-[family-name:var(--mv-font-display)] text-xl font-bold text-[var(--mv-fg-strong)]">{t('auth.2fa.codeTitle')}</h1>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">{t('auth.2fa.codeSubtitle')}</p>
      </div>
      {error && <ErrorBand>{error}</ErrorBand>}
      {!expired && (
        <>
          <CodeField label={t('auth.2fa.code')} value={code} onChange={setCode} hint={t('auth.2fa.codeHint')} autoFocus />
          <Button type="submit" isDisabled={pending || code.trim().length < 6} className="w-full">
            {pending ? t('auth.2fa.verifying') : t('auth.2fa.verify')}
          </Button>
        </>
      )}
      <StartOver onPress={onStartOver} />
    </form>
  )
}

/** Enforced set-up right after the password: the key, a first code, then the recovery codes once. */
function SetupStep({ onSignedIn, onStartOver }: { onSignedIn: (user: SessionUser) => Promise<void>; onStartOver: () => void }) {
  const t = useT()
  const labels = twoFactorLabels(t)
  const [setup, setSetup] = useState<{ secret: string; uri: string } | null>(null)
  const [done, setDone] = useState<{ user: SessionUser; codes: string[] } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [fatal, setFatal] = useState<string | null>(null)
  const [pending, setPending] = useState(false)
  // Strict mode runs effects twice in development; one key is enough.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    api('/auth/2fa/pending/setup', schemas.twoFactorSetup, { method: 'POST' })
      .then(setSetup)
      .catch((err: unknown) => setFatal(twoFactorError(err, t)))
  }, [t])

  const confirm = async (code: string) => {
    setPending(true)
    setError(null)
    try {
      const result = await api('/auth/2fa/pending/confirm', schemas.twoFactorConfirm, { method: 'POST', body: { code } })
      if (result.user) setDone({ user: result.user, codes: result.recoveryCodes })
    } catch (err) {
      if (err instanceof ApiError && err.status === 422) setError(t('auth.2fa.wrongCode'))
      else setFatal(twoFactorError(err, t))
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="mv-panel flex flex-col gap-4 !p-6">
      <div>
        <h1 className="font-[family-name:var(--mv-font-display)] text-xl font-bold text-[var(--mv-fg-strong)]">{done ? labels.recoveryTitle : t('auth.2fa.setupTitle')}</h1>
        {!done && <p className="mt-1 text-sm text-[var(--fg-muted)]">{t('auth.2fa.setupRequired')}</p>}
      </div>
      {fatal && <ErrorBand>{fatal}</ErrorBand>}
      {done ? (
        <RecoveryCodes codes={done.codes} labels={{ ...labels, recoveryTitle: '' }} onDone={() => void onSignedIn(done.user)} />
      ) : (
        !fatal && setup && <TwoFactorSetupForm setup={setup} labels={labels} onSubmit={(code) => void confirm(code)} error={error} pending={pending} />
      )}
      {!done && <StartOver onPress={onStartOver} />}
    </div>
  )
}
